/**
 * Activity-detection helpers used to decide when read time should accrue.
 */

const isDocumentVisible = (): boolean => {
    if (typeof document === 'undefined') {
        return false;
    }

    return !document.hidden;
};

/**
 * Whether the window currently has focus. A tab can be visible (not hidden)
 * while the user is interacting with another window on top of it.
 */
const isWindowFocused = (): boolean => {
    if (typeof document === 'undefined' || typeof document.hasFocus !== 'function') {
        return true;
    }

    return document.hasFocus();
};

/**
 * "Active" means the read time should accrue: the tab is visible AND the window
 * is focused. This is stricter than visibility alone so time doesn't pile up
 * while the user is in another app over a still-visible tab.
 */
export const isUserActive = (): boolean => isDocumentVisible() && isWindowFocused();

/**
 * Subscribe to changes in active state (visibility + window focus/blur).
 */
export const onActivityChange = (callback: (isActive: boolean) => void): (() => void) => {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
        return () => {};
    }

    const handler = () => callback(isUserActive());

    document.addEventListener('visibilitychange', handler);
    window.addEventListener('focus', handler);
    window.addEventListener('blur', handler);

    return () => {
        document.removeEventListener('visibilitychange', handler);
        window.removeEventListener('focus', handler);
        window.removeEventListener('blur', handler);
    };
};
